import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ALL_FIELDS, type FieldCeiling } from "permissions/_views/people/person-record-grants-model";
import {
  ACCESS_LEVEL_NAMES,
  FIELD_NAMES,
  FIELD_NOT_GRANTED,
  fieldCeiling,
  fieldEditorTitle,
} from "permissions/_views/wording/permission-wording";
import { AccessLevel } from "permissions/resources/permission-resource-types";
import type { WorkAgreementField } from "permissions/resources/work/work-agreement-field";
import { useId, useState } from "react";

/** The access level the editor sets on each field it changes. */
export type FieldAccessValues = Partial<Record<WorkAgreementField, AccessLevel>>;

/**
 * The field editor of one proposal or one work agreement grant: every field, with what the grant
 * confers on it (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Per-kind shape").
 *
 * All eight fields are listed, granted or not, so the reader sees what the grant leaves out as well
 * as what it gives. A derived grant is read-only: each field says the most it can be and the grant
 * that set that, and the reader changes it there.
 */

const NOT_GRANTED_VALUE = "none";

const FieldList = styled("dl")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(8rem, auto) minmax(0, 1fr)",
  alignItems: "center",
  gap: theme.spacing(1.5, 2),
  margin: 0,
  [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)", rowGap: theme.spacing(0.5) },
  "& dd": { margin: 0 },
}));

function levelText(level: AccessLevel | undefined): string {
  return level === undefined ? FIELD_NOT_GRANTED : ACCESS_LEVEL_NAMES[level];
}

export type FieldAccessDialogProps = Readonly<{
  open: boolean;
  /** The kind's name as a heading: "One proposal". */
  kindName: string;
  personName: string;
  /** The record, as its row names it. */
  targetName?: string;
  fields: FieldAccessValues;
  /** Set for a derived grant: what caps each field, and the dialog is read-only. */
  ceilings?: Partial<Record<WorkAgreementField, FieldCeiling>>;
  readOnly?: boolean;
  /**
   * Set when the editor makes a new grant (Add access): Save reads this, and an untouched editor can be
   * saved, since a grant that names no field still opens the record.
   */
  grantLabel?: string;
  onClose: () => void;
  /** Saves the fields; resolves true when the server took them. */
  onSave?: (fields: FieldAccessValues) => Promise<boolean>;
}>;

export default function FieldAccessDialog(props: FieldAccessDialogProps) {
  const { open, kindName, personName, targetName, fields, ceilings, grantLabel, onClose, onSave } = props;
  const titleId = useId();
  const readOnly = props.readOnly || ceilings !== undefined || !onSave;
  const [draft, setDraft] = useState<FieldAccessValues>(fields);
  const [saving, setSaving] = useState(false);

  // Each opening starts from what the grant holds now. Done during render, not in an effect.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setDraft(fields);
  }

  const changed = ALL_FIELDS.some((field) => draft[field] !== fields[field]);

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    const saved = await onSave(draft);
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} aria-labelledby={titleId} fullWidth maxWidth="sm">
      <DialogTitle id={titleId}>{fieldEditorTitle(kindName, personName)}</DialogTitle>
      <DialogContent>
        {targetName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {targetName}
          </Typography>
        )}
        <FieldList>
          {ALL_FIELDS.map((field) => {
            const label = FIELD_NAMES[field];
            const value = draft[field];
            const ceiling = ceilings?.[field];
            return (
              <div key={field} style={{ display: "contents" }}>
                <dt>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {label}
                  </Typography>
                </dt>
                <dd>
                  {readOnly ? (
                    <>
                      <Typography variant="body2">{levelText(value)}</Typography>
                      {ceilings && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          {ceiling ? fieldCeiling(ACCESS_LEVEL_NAMES[ceiling.level], ceiling.parent) : "No grant sets this field"}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label={label}
                      value={value === undefined ? NOT_GRANTED_VALUE : String(value)}
                      disabled={saving}
                      onChange={(event) => {
                        const next = event.target.value === NOT_GRANTED_VALUE ? undefined : (Number(event.target.value) as AccessLevel);
                        setDraft((current) => {
                          const updated = { ...current };
                          if (next === undefined) delete updated[field];
                          else updated[field] = next;
                          return updated;
                        });
                      }}
                    >
                      <MenuItem value={NOT_GRANTED_VALUE}>{FIELD_NOT_GRANTED}</MenuItem>
                      <MenuItem value={String(AccessLevel["Read-Only"])}>{ACCESS_LEVEL_NAMES[AccessLevel["Read-Only"]]}</MenuItem>
                      <MenuItem value={String(AccessLevel["Read/Write"])}>{ACCESS_LEVEL_NAMES[AccessLevel["Read/Write"]]}</MenuItem>
                    </TextField>
                  )}
                </dd>
              </div>
            );
          })}
        </FieldList>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button variant="contained" onClick={() => void save()} disabled={(!changed && !grantLabel) || saving}>
            {grantLabel ?? "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
